import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	isBusinessDomain,
	isLikelyPersonalDomain,
	isPersonalDomain,
} from '@/components/signup/utils/is-personal-domain';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import {
	fireNudgeEmailChangedAnalytics,
	fireNudgeShownAnalytics,
} from './recommend-work-email-nudge-analytics';

/**
 * Encapsulates the `recommend-work-email-signup` experiment behaviour for the
 * inline signup input.
 *
 * Enrollment/exposure for this experiment is deferred (the manifest sets
 * `fireExperimentExposure: false`) so that it only counts users who reach the
 * point where the treatment could actually appear — i.e. those whose email is
 * heading toward a known personal / free provider domain. This hook:
 *
 *  - determines eligibility live, as the user types, once the full provider
 *    label is typed (so "ada@gmail" qualifies, but "ada@gmai" does not) —
 *    deliberately independent of the blur-gated `isValid` state so the nudge
 *    appears immediately rather than only after the field loses focus,
 *  - fires the exposure event exactly once for BOTH control and treatment at
 *    the moment the user first becomes eligible (gating on enrollment rather
 *    than the treatment value keeps the A/B comparison valid),
 *  - reports whether the treatment nudge should be shown, latching it "on" once
 *    eligible so it persists even if the email is later edited or cleared, and
 *  - fires the `shown` UI event once when the treatment nudge is first surfaced,
 *    so analysis can distinguish treatment users who actually saw the nudge from
 *    the (control) users who were exposed at the same eligibility point, and
 *  - returns `notifyEmailSubmitted`, which the consumer calls synchronously from
 *    the submit-button click to fire the `emailChanged` conversion event.
 *
 * @param email The current email input value (may be partially typed).
 * @returns `{ showNudge, notifyEmailSubmitted }` — `showNudge` is true for the
 *   enrolled treatment arm from the moment the user first becomes eligible
 *   onward (it does not turn back off); `notifyEmailSubmitted(submittedEmail)`
 *   fires the `emailChanged` event once if the submitted email represents a
 *   genuine personal -> business conversion for a nudged user.
 */
export const useRecommendWorkEmailNudge = ({
	email,
}: {
	email: string;
}): { showNudge: boolean; notifyEmailSubmitted: (submittedEmail: string) => void } => {
	const experiment = useExperiment(experiments.recommendWorkEmailSignup);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Only treat the user as enrolled once Statsig has actually resolved their
	// arm. `type === 'enrolled'` alone is true as soon as the (synchronous)
	// targeting cohort is known, but the bucketed parameter value can still be
	// resolving — during that window `getExperimentValue` returns undefined and
	// the hook falls back to `defaultValue` (false) with `usingDefault: true`.
	// A comment elsewhere in the codebase puts it succinctly: "usingDefault is
	// equivalent to not being enrolled in Statsig".
	//
	// Gating on `usingDefault === false` avoids a race that would unbalance the
	// experiment: a treatment user whose bucket has not resolved yet would
	// otherwise fire an exposure as the default (control) value before latching
	// the treatment UI. Because this gate is independent of which arm the user
	// is in, it keeps control/treatment exposure symmetric.
	//
	// Narrow to a single resolved-enrolled reference so downstream reads of
	// `value` / `fireExposure` are type-safe (the hook returns a discriminated
	// union whose not-enrolled / indeterminate arms lack those fields).
	const resolvedExperiment =
		experiment.type === 'enrolled' && experiment.usingDefault === false ? experiment : undefined;

	// Eligible = enrollment is fully resolved AND the domain typed so far is a
	// known personal provider. Evaluated on every keystroke — no dependency on
	// blur/validation.
	const isEligible = resolvedExperiment !== undefined && isLikelyPersonalDomain(email);

	// Fire the exposure event exactly once, for both arms, on first eligibility.
	// `resolvedExperiment` is checked here both to guard the one-shot ref and to
	// narrow the discriminated union so `fireExposure` is type-safe (TypeScript
	// does not carry the narrowing from `isEligible`, which is a plain boolean).
	const hasFiredExposureRef = useRef(false);
	useEffect(() => {
		if (isEligible && resolvedExperiment && !hasFiredExposureRef.current) {
			resolvedExperiment.fireExposure();
			hasFiredExposureRef.current = true;
		}
	}, [isEligible, resolvedExperiment]);

	// Show the nudge for the enrolled treatment arm once eligible — and then keep
	// it shown. The nudge intentionally "latches": once the user's email is
	// confidently personal we surface the recommendation and leave it in place
	// even if they subsequently edit or clear the field, so it doesn't flicker
	// away mid-correction.
	const hasShownNudgeRef = useRef(false);
	if (isEligible && resolvedExperiment !== undefined && resolvedExperiment.value === true) {
		hasShownNudgeRef.current = true;
	}
	const showNudge = hasShownNudgeRef.current;

	// Fire the `recommendWorkEmailNudge shown` UI event exactly once, the first
	// time the treatment nudge is surfaced. Exposure fires for both arms at the
	// eligibility point, so this event is what identifies, within that exposed
	// population, the treatment users who were actually shown the nudge. It is
	// naturally scoped to personal-domain treatment users because `showNudge`
	// only latches on for the resolved treatment arm once the email is a
	// confident personal domain — the nudge-display logic above is unchanged.
	const hasFiredShownRef = useRef(false);
	useEffect(() => {
		if (showNudge && !hasFiredShownRef.current) {
			hasFiredShownRef.current = true;
			fireNudgeShownAnalytics({ createAnalyticsEvent });
		}
	}, [showNudge, createAnalyticsEvent]);

	// Track whether the user has *ever* held a complete, valid personal email
	// while mounted. This is the "did they start on a personal address" history
	// that makes a later business email at submit time a genuine switch.
	//
	// `hasEverBeenPersonalRef` starts as `false` and is only ever set to `true`
	// (never cleared) — and only *after* an effect run has observed a valid,
	// complete personal address. It deliberately does NOT trust the initial /
	// seeded prop value (which may be a prefilled type-ahead email), so the first
	// render can never, by itself, establish the "was personal" baseline. Only
	// setting it (never clearing) means an intervening cleared/partial value does
	// not erase the fact that the user had entered a personal email — so the
	// switch is still counted even via an indirect path (personal -> clear ->
	// business). Requiring a *complete* personal address also stops partially-
	// typed input between keystrokes from spuriously establishing the baseline.
	const hasEverBeenPersonalRef = useRef(false);
	useEffect(() => {
		if (isPersonalDomain(email)) {
			hasEverBeenPersonalRef.current = true;
		}
	}, [email]);

	// Fire the `recommendWorkEmailNudge emailChanged` track event exactly once,
	// when a nudged user who entered a personal email at some point this session
	// then *submits* the form with a business email. This is the direct
	// "converted to a work email after the nudge, and went through with it" signal.
	//
	// This is exposed as an imperative callback (rather than fired from an effect
	// reacting to a submit flag) because the signup submit handler redirects the
	// page via `window.location.assign` almost immediately. An effect that fires
	// on a re-render triggered by submit would race the navigation and often not
	// run / flush in time. The consumer instead calls this synchronously from the
	// submit-button click — the same in-gesture timing as the other analytics
	// events that already fire just before the redirect — so the event dispatches
	// before the page tears down.
	//
	// Gating: `hasShownNudgeRef` keeps it scoped to users who were actually
	// nudged; `hasEverBeenPersonalRef` ensures they had entered a personal email
	// first; and `isBusinessDomain(submittedEmail)` confirms the email they are
	// submitting with is a valid business domain. `hasFiredEmailChangedRef`
	// guarantees at most one fire per session (e.g. across submit retries). All
	// state is read from refs so the callback is stable and always sees current
	// values.
	const hasFiredEmailChangedRef = useRef(false);
	const notifyEmailSubmitted = useCallback(
		(submittedEmail: string) => {
			const switchedFromPersonalToBusiness =
				hasEverBeenPersonalRef.current && isBusinessDomain(submittedEmail);

			if (
				hasShownNudgeRef.current &&
				!hasFiredEmailChangedRef.current &&
				switchedFromPersonalToBusiness
			) {
				hasFiredEmailChangedRef.current = true;
				fireNudgeEmailChangedAnalytics({ createAnalyticsEvent });
			}
		},
		[createAnalyticsEvent],
	);

	return { showNudge, notifyEmailSubmitted };
};
